import React from 'react'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { useTranslation } from 'react-i18next'
import { CalendarIcon } from './Icons'

interface DealCustomer {
  id: number
  customer_id: number
  name: string
  email?: string
  phone?: string
  role: 'applicant' | 'co_applicant'
  order_position: number
}

interface CommissionSplit {
  id: number
  partner_id?: number
  split_type: 'percentage' | 'fixed_amount'
  split_value: number
  partner_name?: string
  partner_first_name?: string
  partner_last_name?: string
}

interface Deal {
  id: number
  name: string
  title?: string
  amount: string
  customer_name: string
  customer_phone?: string
  customer_email?: string
  customer_id: number
  deal_type_id?: string
  type_name?: string
  lender_id?: number | null
  lender_name?: string | null
  commission?: {
    totalPoints: number
    totalPercentage: number
    grossCommission: number
    agencyFee: number
    closingFeeAmount: number
    netCommission: number
  }
  commission_splits?: CommissionSplit[]
  base_points: string
  bonus_points: string
  volume_points: string
  closing_date?: string
  expected_close_date?: string
  status_id: string
  status_name: string
  status_color: string
  status_is_archived?: boolean
  position: number
  description?: string
  created_at: string
  updated_at: string
  deal_customers?: DealCustomer[]
}

interface DealStatus {
  id: number
  name: string
  color: string
  position: number
  is_archived?: boolean
}

interface DealsCompactViewProps {
  statuses: DealStatus[]
  dealsByStatus: { [statusId: string]: Deal[] }
  onDragEnd: (result: DropResult) => void
  onDealClick: (deal: Deal) => void
  className?: string
  showEmptyStatuses?: boolean
  onToggleEmptyStatuses?: () => void
}

const DealsCompactView: React.FC<DealsCompactViewProps> = ({
  statuses,
  dealsByStatus,
  onDragEnd,
  onDealClick,
  className = '',
  showEmptyStatuses = true,
  onToggleEmptyStatuses
}) => {
  const { t } = useTranslation()
  
  // Format amount as K or M
  const formatCompactCurrency = (amount: string | number): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount
    if (isNaN(num) || num === 0) return '$0'
    
    if (num >= 1000000) {
      // Format as millions (e.g., 1.23M)
      return `$${(num / 1000000).toFixed(2)}M`
    } else if (num >= 1000) {
      // Format as thousands (e.g., 400K)
      return `$${Math.round(num / 1000)}K`
    } else {
      // Less than 1000, show full amount
      return `$${num.toLocaleString()}`
    }
  }
  
  // Get all deals in a flat array with status information
  const allDeals = statuses.flatMap(status => 
    (dealsByStatus[String(status.id)] || []).map(deal => ({
      ...deal,
      statusInfo: status
    }))
  )

  const formatCurrency = (amount: string | number) => {
    // Handle edge cases where amount might be an object or invalid value
    if (amount === null || amount === undefined || amount === '') {
      return '$0'
    }
    
    // If it's an object, try to extract a numeric value or default to 0
    if (typeof amount === 'object') {
      return '$0'
    }
    
    const numericAmount = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.-]+/g, '')) : amount
    if (isNaN(numericAmount) || numericAmount === 0) return '$0'
    
    // Compact format - use K and M suffixes
    if (numericAmount >= 1000000) {
      return `$${(numericAmount / 1000000).toFixed(1)}M`
    } else if (numericAmount >= 1000) {
      return `$${(numericAmount / 1000).toFixed(0)}K`
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericAmount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })
  }

  // Filter statuses based on showEmptyStatuses setting
  const visibleStatuses = showEmptyStatuses 
    ? statuses 
    : statuses.filter(status => (dealsByStatus[String(status.id)] || []).length > 0)

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden ${className}`}>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="p-4">
          {/* Header */}
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Deals - Compact View
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {allDeals.length} deal{allDeals.length !== 1 ? 's' : ''} across {visibleStatuses.length} stage{visibleStatuses.length !== 1 ? 's' : ''}
                </p>
              </div>
              {onToggleEmptyStatuses && (
                <button
                  onClick={onToggleEmptyStatuses}
                  className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <span>{showEmptyStatuses ? 'Hide' : 'Show'} empty</span>
                  <div className={`w-3 h-3 border border-gray-400 rounded flex items-center justify-center ${showEmptyStatuses ? 'bg-gray-800 border-gray-900' : ''}`}>
                    {showEmptyStatuses && (
                      <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Compact List */}
          <div className="space-y-2">
            {visibleStatuses.map((status) => {
              const statusDeals = dealsByStatus[String(status.id)] || []

              return (
                <Droppable key={status.id} droppableId={status.id.toString()}>
                  {(provided, snapshot) => (
                    <div className="space-y-2">
                      {/* Status Header */}
                      <div className="flex items-center space-x-2 py-2 border-b border-gray-200 dark:border-gray-700">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: status.color }}
                        />
                        <span className="font-medium text-gray-900 dark:text-white text-sm">
                          {status.name}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400 text-xs">
                          ({statusDeals.length})
                        </span>
                      </div>

                      {/* Deals List */}
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`space-y-1 ${
                          snapshot.isDraggingOver ? 'bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2' : ''
                        }`}
                      >
                        {statusDeals.length === 0 ? (
                          <div className="p-4 text-center text-gray-400 dark:text-gray-500 text-sm italic border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-lg">
                            No deals in this status
                          </div>
                        ) : (
                          statusDeals.map((deal, index) => (
                          <Draggable key={deal.id} draggableId={deal.id.toString()} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                onClick={() => onDealClick(deal)}
                                className={`group relative p-4 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-100 dark:border-gray-700 cursor-pointer hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-lg transition-all duration-200 ${
                                  snapshot.isDragging ? 'shadow-2xl rotate-2 scale-105 border-blue-400 dark:border-blue-500' : ''
                                }`}
                              >
                                <div className="space-y-3">
                                  {/* Header - Customer Name & Deal Type */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <span className="font-semibold text-gray-900 dark:text-white text-base truncate">
                                        {deal.deal_customers && deal.deal_customers.length > 0
                                          ? deal.deal_customers.find(dc => dc.role === 'applicant')?.name || deal.customer_name
                                          : deal.customer_name
                                        }
                                      </span>
                                      
                                      {deal.type_name && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700">
                                          {deal.type_name}
                                        </span>
                                      )}
                                    </div>

                                    {deal.expected_close_date && (
                                      <div className="flex items-center text-blue-600 dark:text-blue-400 text-xs font-medium bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 rounded-lg border border-blue-200 dark:border-blue-700">
                                        <CalendarIcon size={12} className="mr-1.5" />
                                        <span>{formatDate(deal.expected_close_date)}</span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Amount & Lender Row */}
                                  <div className="flex items-center justify-between">
                                    {/* Amount - Large and prominent */}
                                    <div className="flex items-baseline gap-2">
                                      <span className="text-2xl font-bold text-green-600 dark:text-green-400 tabular-nums">
                                        {formatCompactCurrency(deal.amount)}
                                      </span>
                                      {deal.commission && deal.commission.netCommission > 0 && (() => {
                                        // Calculate broker commission after splits
                                        const splitAmount = (deal.commission_splits || []).reduce((sum, split) => {
                                          const amount = split.split_type === 'percentage'
                                            ? (deal.commission!.netCommission * (split.split_value / 100))
                                            : split.split_value
                                          return sum + amount
                                        }, 0)
                                        const brokerCommission = deal.commission.netCommission - splitAmount
                                        
                                        return (
                                          <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                                            Net: <span className="text-green-600 dark:text-green-400">{formatCompactCurrency(brokerCommission)}</span>
                                          </span>
                                        )
                                      })()}
                                    </div>

                                    {/* Lender Badge */}
                                    {deal.lender_name && (
                                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                                        <svg className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                                          <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                                        </svg>
                                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                          {deal.lender_name}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                          ))
                        )}
                        {provided.placeholder}
                      </div>
                    </div>
                  )}
                </Droppable>
              )
            })}
          </div>
        </div>
      </DragDropContext>
    </div>
  )
}

export default DealsCompactView